/**
 * voice.js — human vocalisations, synthesised by source-filter modelling.
 *
 * A voice is a buzzing glottal source shaped by the resonances of the vocal tract.
 * Model it literally: a sawtooth at the fundamental (the vocal folds), rolled off,
 * driven through three parallel bandpass filters at the formant frequencies of a
 * vowel, plus a breath-noise channel through the same formants.
 *
 * Nobody will mistake this for a recorded actor, but it lands unmistakably as *a
 * person in pain* rather than as a synthesiser, because the formant ratios and the
 * falling pitch contour are the cues the ear actually uses. Adding a rasp channel
 * (noise modulated into the source) is what turns a groan into a scream.
 *
 * Formant tables are the standard adult-male values.
 */

import {
  noiseSource, startNoise, noiseBurst, perc, shape, biquad, sweep, waveshaper,
  gain, chain, partial, rand,
} from './dsp.js';

export const VOWELS = {
  ah: [[730, 1.0], [1090, 0.45], [2440, 0.16]],
  uh: [[520, 1.0], [1190, 0.4], [2390, 0.14]],
  oh: [[570, 1.0], [840, 0.5], [2410, 0.1]],
  eh: [[530, 1.0], [1840, 0.5], [2480, 0.2]],
  ee: [[270, 1.0], [2290, 0.55], [3010, 0.25]],
};

/**
 * @param cfg.f0        starting fundamental (Hz)
 * @param cfg.f0End     ending fundamental — falling reads as exhaustion/pain, rising as alarm
 * @param cfg.dur       total duration
 * @param cfg.vowel     key into VOWELS, or an explicit formant table
 * @param cfg.rasp      0..1 amount of noise fed through the source, i.e. how strained
 * @param cfg.breath    0..1 amount of unvoiced air
 * @param cfg.attack    onset time — a shout has a fast onset, a groan a slow one
 */
export function vocal(ac, dest, t, rng, cfg = {}) {
  const {
    f0 = 118, f0End = 82, dur = 0.4, vowel = 'ah', rasp = 0.35, breath = 0.3,
    level = 1, attack = 0.025, vibrato = 5.5, vibratoAmt = 14, drive = 1.8, tilt = 2600,
  } = cfg;
  const formants = Array.isArray(vowel) ? vowel : (VOWELS[vowel] ?? VOWELS.ah);
  const lv = level * (1 + (rng() - 0.5) * 0.16);

  // Output stage: a common gain envelope, then mild saturation for chest resonance.
  const out = gain(ac, 1);
  const amp = shape(ac, t, [
    [0, 0],
    [attack, lv],
    [attack + dur * 0.28, lv * 0.85],
    [dur * 0.75, lv * 0.4],
    [dur, 1e-5],
  ]);
  chain(out, amp, waveshaper(ac, drive), gain(ac, 0.8), dest);

  // --- glottal source ---
  const src = gain(ac, 1);
  const vib = ac.createOscillator();
  vib.type = 'sine';
  vib.frequency.setValueAtTime(vibrato * rand(rng, 0.8, 1.25), t);
  const vibAmt = gain(ac, vibratoAmt * rand(rng, 0.6, 1.4));
  vib.connect(vibAmt);
  vib.start(t); vib.stop(t + dur + 0.05);

  const jitter = 1 + (rng() - 0.5) * 0.1;
  for (let i = 0; i < 2; i++) {
    const o = ac.createOscillator();
    o.type = 'sawtooth';
    const a = f0 * jitter * (1 + i * 0.008);
    const b = f0End * jitter * (1 + i * 0.008);
    o.frequency.setValueAtTime(a * 1.08, t);
    o.frequency.exponentialRampToValueAtTime(a, t + Math.min(0.05, dur * 0.15));
    o.frequency.exponentialRampToValueAtTime(Math.max(30, b), t + dur);
    vibAmt.connect(o.detune);
    chain(o, gain(ac, i === 0 ? 0.55 : 0.28), src);
    o.start(t); o.stop(t + dur + 0.05);
  }
  // Rasp: broadband energy locked to the source, bandpassed high. Strain, not hiss.
  if (rasp > 0) {
    const r = noiseSource(ac, 'white');
    const rbp = biquad(ac, 'bandpass', f0 * 9, 0.8);
    sweep(rbp.frequency, t, f0 * 10, f0End * 8, dur);
    chain(r, rbp, gain(ac, rasp * 0.45), src);
    startNoise(r, t, dur + 0.05, rng);
  }
  // Glottal spectral tilt — a raw sawtooth is far too bright to be a voice.
  const tiltF = biquad(ac, 'lowpass', tilt, 0.6);
  const shelf = biquad(ac, 'highshelf', 2200, 0.7, -8);
  const shaped = chain(src, tiltF, shelf);

  // --- vocal tract: parallel formant resonators ---
  for (let i = 0; i < formants.length; i++) {
    const [f, g] = formants[i];
    const bp = biquad(ac, 'bandpass', f * rand(rng, 0.94, 1.07), 7 + i * 3);
    // Formants move as the mouth relaxes through the sound.
    sweep(bp.frequency, t, f * rand(rng, 1.0, 1.12), f * rand(rng, 0.86, 0.98), dur);
    chain(shaped, bp, gain(ac, g * 1.4), out);
  }
  // A little unfiltered source keeps the low end present.
  chain(shaped, gain(ac, 0.18), out);

  // --- breath ---
  if (breath > 0) {
    const b = noiseSource(ac, 'pink');
    const bbp = biquad(ac, 'bandpass', 1300, 0.8);
    sweep(bbp.frequency, t, 1000, 2200, dur);
    const bg = shape(ac, t, [[0, 0], [dur * 0.2, breath * 0.4], [dur * 0.7, breath * 0.5], [dur * 1.15, 1e-5]]);
    chain(b, bbp, bg, dest);
    startNoise(b, t, dur * 1.2, rng);
  }
}

/* --------------------------------------------------------------- reactions --- */

export function hurt(ac, dest, t, rng) {
  vocal(ac, dest, t, rng, {
    f0: rand(rng, 135, 165), f0End: rand(rng, 88, 105), dur: rand(rng, 0.26, 0.36),
    vowel: rng() < 0.5 ? 'uh' : 'ah', rasp: 0.55, breath: 0.35,
    attack: 0.012, level: 0.9, drive: 2.2,
  });
  // The involuntary intake that precedes it.
  noiseBurst(ac, dest, t - 0.0, rng, {
    filter: 'bandpass', freq: 2400, Q: 0.9, level: 0.14, attack: 0.006, decay: 0.05,
  });
}

export function death(ac, dest, t, rng) {
  // Long, falling, increasingly breathy: the voice runs out of support.
  vocal(ac, dest, t, rng, {
    f0: rand(rng, 120, 145), f0End: rand(rng, 52, 68), dur: rand(rng, 0.85, 1.1),
    vowel: 'uh', rasp: 0.7, breath: 0.55, attack: 0.03, level: 0.95, drive: 2.4,
    vibrato: 4, vibratoAmt: 22,
  });
  vocal(ac, dest, t + 0.55, rng, {
    f0: rand(rng, 80, 95), f0End: rand(rng, 42, 52), dur: 0.55,
    vowel: 'oh', rasp: 0.5, breath: 0.9, attack: 0.09, level: 0.4, drive: 1.5,
  });
  const b = noiseSource(ac, 'pink');
  const bp = biquad(ac, 'bandpass', 900, 0.7);
  sweep(bp.frequency, t + 0.7, 1200, 500, 0.8);
  const g = shape(ac, t + 0.7, [[0, 0], [0.15, 0.16], [0.8, 1e-5]]);
  chain(b, bp, g, dest);
  startNoise(b, t + 0.7, 0.9, rng);
}

export function enemyPain(ac, dest, t, rng) {
  vocal(ac, dest, t, rng, {
    f0: rand(rng, 155, 200), f0End: rand(rng, 100, 130), dur: rand(rng, 0.3, 0.45),
    vowel: rng() < 0.6 ? 'ah' : 'eh', rasp: 0.75, breath: 0.3,
    attack: 0.008, level: 0.95, drive: 2.8, vibratoAmt: 26,
  });
}

export function enemyDeath(ac, dest, t, rng) {
  vocal(ac, dest, t, rng, {
    f0: rand(rng, 150, 185), f0End: rand(rng, 55, 75), dur: rand(rng, 0.9, 1.2),
    vowel: 'ah', rasp: 0.8, breath: 0.5, attack: 0.006, level: 1.0, drive: 3.0,
    vibrato: 6, vibratoAmt: 30,
  });
  // Body hitting the floor, roughly when the voice gives out.
  const at = t + rand(rng, 0.55, 0.8);
  partial(ac, dest, at, rand(rng, 70, 95), 0.5, 0.12, { glideTo: 48, attack: 0.003 });
  noiseBurst(ac, dest, at, rng, {
    filter: 'lowpass', freq: 700, Q: 0.9, level: 0.45, attack: 0.002, decay: 0.09,
  });
  noiseBurst(ac, dest, at + 0.02, rng, {
    type: 'pink', filter: 'bandpass', freq: 1600, Q: 0.8, level: 0.2, attack: 0.01, decay: 0.14,
  });
}

/**
 * A shout over squad comms. The vocal is deliberately *bandlimited and clipped* —
 * 300–3000 Hz plus hard saturation is the sound of a radio, and the ear reads that
 * processing as "someone else, somewhere else" faster than it reads any word.
 */
export function enemyAlert(ac, dest, t, rng) {
  const bus = gain(ac, 1);
  const hp = biquad(ac, 'highpass', 380, 0.8);
  const lp = biquad(ac, 'lowpass', 2900, 0.9);
  const pk = biquad(ac, 'peaking', 1600, 1.4, 7);
  chain(bus, hp, pk, lp, waveshaper(ac, 3.2), gain(ac, 0.85), dest);

  // Two syllables: a rising call, then a falling one.
  vocal(ac, bus, t, rng, {
    f0: rand(rng, 140, 175), f0End: rand(rng, 195, 240), dur: 0.22,
    vowel: 'eh', rasp: 0.5, breath: 0.15, attack: 0.01, level: 1.0, drive: 1.6,
  });
  vocal(ac, bus, t + 0.26, rng, {
    f0: rand(rng, 200, 240), f0End: rand(rng, 120, 150), dur: 0.34,
    vowel: 'ah', rasp: 0.6, breath: 0.2, attack: 0.012, level: 1.0, drive: 1.6,
  });
  // Squelch tail — the carrier dropping.
  noiseBurst(ac, dest, t + 0.62, rng, {
    filter: 'bandpass', freq: 2200, Q: 0.9, level: 0.16, attack: 0.002, decay: 0.045,
  });
  noiseBurst(ac, dest, t - 0.0, rng, {
    filter: 'bandpass', freq: 1800, Q: 1.1, level: 0.12, attack: 0.001, decay: 0.02,
  });
}

/**
 * Heartbeat for low-health. Two thumps, the second softer and slightly higher, with
 * a wide gap — lub-DUP. Almost pure sub content, so it is felt rather than heard and
 * does not fight the rest of the mix.
 */
export function heartbeat(ac, dest, t, rng) {
  const beat = (at, lv, f) => {
    const o = ac.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f * 2.1, at);
    o.frequency.exponentialRampToValueAtTime(f * 0.62, at + 0.09);
    const g = perc(ac, at, lv, 0.008, 0.13);
    chain(o, g, dest);
    o.start(at); o.stop(at + 0.25);
    noiseBurst(ac, dest, at, rng, {
      filter: 'lowpass', freq: 220, Q: 1.2, level: lv * 0.35, attack: 0.006, decay: 0.09,
    });
  };
  beat(t, 0.95, rand(rng, 52, 60));
  beat(t + 0.21, 0.6, rand(rng, 62, 72));
}
